import React, { useCallback } from 'react';
import { generatePath } from 'react-router-dom';
import { CellProps } from 'react-table';
import { Button, Center, Flex, Image } from '@chakra-ui/react';

import { AddEditItemLink, ErrorMessages, MainPageContent, PageHeading, Table } from '../../common/components';
import { useDebounce } from '../../common/hooks/useDebounce';
import { useExecuteWithConfirmation } from '../../common/utils';
import { config } from '../../config/config';
import { DEBOUNCE_SEARCH_TIME } from '../../config/configConsts';
import { Routes } from '../../config/routes';
import { useTranslations } from '../../contexts/LocalizationContext';
import { useDeleteNewsNew, useNewsNewList } from '../../services/news-new';
import { NewsNewResponse } from './models/news-new-response';

export const NewsNew = () => {
  const translations = useTranslations();
  const [page, setPage] = React.useState(1);
  const [searchText, setSearchText] = React.useState('');
  const debouncedSearch = useDebounce(searchText, DEBOUNCE_SEARCH_TIME);

  const {
    data: paginatedNews,
    isFetching: isFetchingNews,
    error: loadNewsError,
  } = useNewsNewList({
    searchText: debouncedSearch,
    limit: config.paginationSize,
    page,
  });

  const { mutate: deleteNews, isLoading: isDeletingNews, error: deleteError } = useDeleteNewsNew();

  const { executeWithConfirmation: onDeleteNews } = useExecuteWithConfirmation(
    translations('news_new_delete_confirmation'),
    deleteNews,
  );

  const handleDelete = useCallback(
    (original: NewsNewResponse) => {
      return () => onDeleteNews(original.id);
    },
    [onDeleteNews],
  );

  const columns = React.useMemo(
    () => [
      {
        id: '1',
        Header: translations('news_new_table_title'),
        accessor: 'title' as const,
      },
      {
        id: '2',
        Header: translations('news_new_table_subtitle'),
        accessor: 'subtitle' as const,
      },
      {
        id: '3',
        Header: translations('news_new_table_language'),
        accessor: 'language' as const,
      },
      {
        id: '4',
        Header: translations('news_new_table_image'),
        accessor: 'imageUrl' as const,
        Cell: ({ cell: { value } }: CellProps<NewsNewResponse>) => (
          <Center>
            <Image width="200px" src={value} />
          </Center>
        ),
      },
      {
        id: 'actions',
        Header: translations('news_new_table_actions'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<NewsNewResponse>) => (
          <Flex justifyContent={'center'}>
            <AddEditItemLink
              size="sm"
              to={generatePath(Routes.UPDATE_NEWS_NEW_PATH, { id: original.id })}
              m={1}
              isEdit
            />
            <Button size="sm" variant="critical" m={1} onClick={handleDelete(original)}>
              {translations('delete')}
            </Button>
          </Flex>
        ),
      },
    ],
    [translations, handleDelete],
  );

  return (
    <MainPageContent>
      <PageHeading>{translations('news_new_header')}</PageHeading>
      <ErrorMessages errors={[loadNewsError, deleteError]} />
      <Table<NewsNewResponse>
        isLoading={isFetchingNews || isDeletingNews}
        data={paginatedNews?.items}
        columns={columns}
        page={page}
        onPageChanged={setPage}
        totalPages={paginatedNews?.totalPages}
        searchText={searchText}
        onSearchTextChanged={setSearchText}
        searchboxPlaceholder={translations('news_new_search_box')}
        searchEnabled
        paginationEnabled
      >
        <Flex justifyContent="flex-end">
          <AddEditItemLink to={Routes.CREATE_NEWS_NEW_PATH}>{translations('news_new_add')}</AddEditItemLink>
        </Flex>
      </Table>
    </MainPageContent>
  );
};
